import { Account } from 'services/connectivity/accounts/types';

import { useAccounts } from './use-accounts';
import { Features, useFetchFeatures } from './use-fetch-features';
import { useFetchSubscriptions } from './use-fetch-subscriptions';

type Subscription = {
  quantity: number;
  plan: string;
};

type Props = {
  features?: Features;
  subscriptions?: Record<string, Subscription>;
  accounts?: Account[];
  product: string;
};

export const getCanJoinProduct = ({ features, subscriptions, accounts, product }: Props) => {
  const accountsInLC = accounts.filter(({ roles, properties }) =>
    roles.find((role) => role.product === 'LiveChat' && !properties.livechat?.suspended)
  );

  const isLC = product === 'LiveChat';
  const LCSubscriptionData = subscriptions?.livechat;
  const subscriptionSeats = LCSubscriptionData?.quantity ?? 0;
  const freeSlotsInLC = subscriptionSeats - accountsInLC.length;
  const canJoinWithoutUpgrade = freeSlotsInLC > 0;
  const joiningBlocked = isLC && !features?.can_upgrade_subscription && !canJoinWithoutUpgrade;
  const isLCStarterPlan = isLC && LCSubscriptionData?.plan === 'starter';

  return {
    isLC,
    joiningBlocked,
    isLCStarterPlan,
  };
};

export const useCanJoinToProduct = (product: string) => {
  const { data: features, isFetching: isFetchingFeatures } = useFetchFeatures();
  const { accounts, isFetching: isFetchingAccounts } = useAccounts();
  const { data: subscriptionsData, isFetching: isFetchingSubscriptions } = useFetchSubscriptions();

  const { joiningBlocked, isLCStarterPlan, isLC } = getCanJoinProduct({
    product,
    accounts,
    features,
    subscriptions: subscriptionsData?.subscriptions,
  });

  return {
    joiningBlocked: joiningBlocked || isLCStarterPlan,
    isLCStarterPlan,
    canUpgradeSubscription: isLC ? features?.can_upgrade_subscription : true,
    isProcessing: isFetchingFeatures || isFetchingAccounts || isFetchingSubscriptions,
  };
};
